import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import FeedbackForm from './FeedbackForm';

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_COLORS = {
  suggestion: 'bg-blue-100 text-blue-700',
  problem: 'bg-red-100 text-red-700',
  feature: 'bg-purple-100 text-purple-700',
  praise: 'bg-green-100 text-green-700',
};
const STATUS_COLORS = {
  new: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
};

export default function ClientFeedback({ getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/client/feedback/mine`, { headers: getHeaders() });
      const data = await res.json();
      if (res.ok) setItems(data.feedback || []);
    } catch (e) { /* noop */ }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="client-feedback-page">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('feedback.title', 'Share your feedback')}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{t('feedback.subtitle', 'Your ideas and issues help us build a better platform.')}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <FeedbackForm getHeaders={getHeaders} onSubmitted={load} />
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('feedback.historyTitle', 'Your submissions')}</h3>
            {items.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center" data-testid="feedback-empty">{t('feedback.empty', 'No feedback yet. Be the first to share an idea!')}</p>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-auto">
                {items.map((f) => (
                  <div key={f.id} className="border border-gray-100 rounded-lg p-3" data-testid={`feedback-item-${f.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[10px] ${CAT_COLORS[f.category] || ''}`}>{t(`feedback.categories.${f.category}`, f.category)}</Badge>
                      <Badge className={`text-[10px] ${STATUS_COLORS[f.status] || ''}`}>{t(`feedback.status.${f.status}`, f.status)}</Badge>
                      <span className="ml-auto text-[10px] text-gray-400">{(f.created_at || '').slice(0, 10)}</span>
                    </div>
                    <p className="text-sm text-gray-600">{f.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
